import React from 'react';

const PlantMaintenancePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              Plant Maintenance
            </h1>
            <p className="text-lg md:text-2xl text-secondary-100 max-w-3xl mx-auto">
              Comprehensive maintenance solutions for industrial facilities
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Image Section - Left side on desktop, top on mobile */}
          <div className="lg:sticky lg:top-36 order-1 lg:order-1 animate-fade-in-left">
            <div className="rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
              <img
                src="/pm.png"
                alt="Plant Maintenance"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Content Section - Right side on desktop, bottom on mobile */}
          <div className="space-y-6 md:space-y-8 order-2 lg:order-2 animate-fade-in-right">
            {/* Company Overview */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="bg-primary-50 p-6 rounded-lg shadow-md border-l-4 border-primary-500">
                <h2 className="text-xl md:text-2xl font-bold text-primary-500 mb-3">
                  Our Expertise
                </h2>
                <p className="text-sm md:text-base text-gray-700 mb-3">
                  We offer{' '}
                  <strong>
                    in-depth knowledge, experience, and technical expertise
                  </strong>{' '}
                  across multiple industrial disciplines.
                </p>
                <p className="text-sm md:text-base text-gray-700">
                  Our commitment to offering creative solutions has earned us
                  the confidence of our clients in various projects that are
                  small or large, simple or complex.
                </p>
              </div>
            </div>

            {/* Erection Services */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.2s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Erection Services
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Piping Erection',
                  'Steel Structure Erection',
                  'Equipment Installation',
                  'Machinery Setup',
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fade-in hover:shadow-md transition-shadow duration-200 flex items-center space-x-3"
                    style={{ animationDelay: `${0.25 + index * 0.05}s` }}
                  >
                    <div className="flex-shrink-0 w-3 h-3 bg-primary-500 rounded-full"></div>
                    <span className="text-sm md:text-base font-medium text-gray-800">
                      {service}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Site Erection */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.3s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Site Erection & Installation
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  'Vessel Platform Erection',
                  'Sluice Gate Installation',
                  'Removal Panel Wall',
                  'Chute Plate Replacement',
                  'Girder Beam Installation',
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fade-in hover:shadow-md transition-shadow duration-200 flex items-center space-x-3"
                    style={{ animationDelay: `${0.35 + index * 0.05}s` }}
                  >
                    <div className="flex-shrink-0 w-3 h-3 bg-primary-500 rounded-full"></div>
                    <span className="text-sm md:text-base font-medium text-gray-800">
                      {service}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Specialized Repairs */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.4s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Specialized Repairs & Maintenance
              </h3>
              <div className="space-y-3">
                {[
                  'Billet Chain Transfer Roller & Screw Rectification',
                  'Refractory Header Cutting',
                  'Catch Pot Repair',
                  'Reformer Leak Arrest',
                ].map((service, index) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-fade-in hover:shadow-md transition-shadow duration-200"
                    style={{ animationDelay: `${0.45 + index * 0.05}s` }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-primary-500 rounded-full mt-2"></div>
                      <span className="text-sm md:text-base font-medium text-gray-800">
                        {service}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Comprehensive Services */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.5s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Comprehensive Maintenance Services
              </h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Installation & Maintenance
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-gray-700">
                      <li className="flex items-start space-x-2">
                        <span className="text-primary-500">•</span>
                        <span>Industrial machinery installation</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary-500">•</span>
                        <span>Equipment maintenance & repair</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary-500">•</span>
                        <span>Specialized tool operations</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Specialized Tools
                    </h4>
                    <ul className="space-y-2 text-sm md:text-base text-gray-700">
                      <li className="flex items-start space-x-2">
                        <span className="text-primary-500">•</span>
                        <span>Welders & hydraulic bolters</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary-500">•</span>
                        <span>Alignment equipment</span>
                      </li>
                      <li className="flex items-start space-x-2">
                        <span className="text-primary-500">•</span>
                        <span>Heavy machinery relocation</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Machine Services */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.6s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Machine Services
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Parts & Components
                  </h4>
                  <p className="text-sm md:text-base text-gray-700">
                    Alignment and replacement of individual machine parts with
                    precision and expertise.
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Machinery Relocation
                  </h4>
                  <p className="text-sm md:text-base text-gray-700">
                    Professional moving and relocation of machinery on-site and
                    off-site.
                  </p>
                </div>
              </div>
            </div>

            {/* Annual Maintenance */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.7s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Annual Maintenance Shutdown Support
              </h3>
              <div className="bg-primary-50 p-6 rounded-lg shadow-md border-l-4 border-primary-500">
                <p className="text-sm md:text-base text-gray-700 mb-3">
                  <strong>
                    Support services for Annual maintenance shutdown in various
                    plants
                  </strong>
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Shutdown Planning
                    </h4>
                    <ul className="text-sm md:text-base text-gray-700 space-y-1">
                      <li>• Pre-shutdown preparation</li>
                      <li>• Resource allocation</li>
                      <li>• Timeline management</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-2">
                      Execution Support
                    </h4>
                    <ul className="text-sm md:text-base text-gray-700 space-y-1">
                      <li>• 24/7 maintenance support</li>
                      <li>• Emergency repairs</li>
                      <li>• Quality assurance</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Standards */}
            <div
              className="animate-slide-up"
              style={{ animationDelay: '0.8s' }}
            >
              <h3 className="text-xl md:text-2xl font-bold text-primary-500 mb-4">
                Service Standards
              </h3>
              <div className="flex flex-wrap gap-3">
                {[
                  'Technical Expertise',
                  'Creative Solutions',
                  'Project Flexibility',
                  'Client Confidence',
                  'Quality Assurance',
                  'Safety Standards',
                  '24/7 Support',
                  'Emergency Response',
                ].map((standard, index) => (
                  <span
                    key={index}
                    className="bg-primary-100 text-primary-500 px-4 py-2 rounded-full text-sm font-medium animate-fade-in hover:bg-primary-200 transition-colors duration-200"
                    style={{ animationDelay: `${0.85 + index * 0.05}s` }}
                  >
                    {standard}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="bg-primary-500 text-secondary-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Need Plant Maintenance Support?
          </h2>
          <p className="text-lg md:text-xl text-secondary-100 mb-8 max-w-2xl mx-auto">
            From routine maintenance to complex shutdowns, we provide
            comprehensive solutions for all your industrial maintenance needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="bg-secondary-500 text-primary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-100 transition-colors cursor-pointer"
            >
              Request Service
            </a>
            <a
              href="/services"
              className="border-2 border-secondary-500 text-secondary-500 px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
            >
              View All Services
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlantMaintenancePage;
